import React, {useEffect, useRef, useState} from 'react';
import {ProjectCarouselType} from '../../../utils/types/Partials';
import {motion} from 'framer-motion';
import {pageTransition} from '../../../utils/data/Global';
import {ProgressiveImage} from '../../../components/Image';
import useWindowSize from '../../../utils/hooks/useWindowSize';
import {getCarouselOffset} from '../../../utils/helpers/carouselOffset';

const DRAG_THRESHOLD = 5;
const VISIBILITY_TOLERANCE = 1;
const WHEEL_IDLE_MS = 200;

const ProjectCarousel: React.FC<ProjectCarouselType> = ({setPopup, setPopupImage, value, project, carouselRef, setActive, setPointer}) => {
    const windowSize = useWindowSize();
    const [isDragging, setIsDragging] = useState(false);
    const [isWheeling, setIsWheeling] = useState(false);
    const isDraggingRef = useRef(false);
    const movedRef = useRef(false);
    const startXRef = useRef(0);
    const startOffsetRef = useRef(0);
    const pointerIdRef = useRef<number | null>(null);
    const imgRefs = useRef<(HTMLDivElement | null)[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const wheelIdleTimeoutRef = useRef<number | null>(null);
    const cursorMergedRef = useRef(false);

    const count = value?.attributes?.Carousel?.data?.length ?? 0;

    const getCurrentOffset = () => {
        const transform = carouselRef.current?.style.transform;
        const match = transform && transform.match(/translateX\((-?[\d.]+)px\)/);
        return match ? -parseFloat(match[1]) : 0;
    };

    // Picks whichever slide is fully within the carousel viewport (closest to
    // its center); if none is fully visible mid-drag, falls back to whichever
    // slide has the most of itself in view.
    const updateActiveByVisibility = () => {
        if (!setActive) return;
        const container = containerRef.current;
        if (!container) return;
        const containerRect = container.getBoundingClientRect();
        const containerCenter = (containerRect.left + containerRect.right) / 2;

        let fullyVisibleIndex = -1;
        let fullyVisibleDist = Infinity;
        let bestOverlapIndex = -1;
        let bestOverlap = -Infinity;

        imgRefs.current.forEach((el, index) => {
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const fullyVisible = rect.left >= containerRect.left - VISIBILITY_TOLERANCE && rect.right <= containerRect.right + VISIBILITY_TOLERANCE;
            if (fullyVisible) {
                const dist = Math.abs((rect.left + rect.right) / 2 - containerCenter);
                if (dist < fullyVisibleDist) {
                    fullyVisibleDist = dist;
                    fullyVisibleIndex = index;
                }
            }
            const overlap = Math.min(rect.right, containerRect.right) - Math.max(rect.left, containerRect.left);
            if (overlap > bestOverlap) {
                bestOverlap = overlap;
                bestOverlapIndex = index;
            }
        });

        const chosen = fullyVisibleIndex >= 0 ? fullyVisibleIndex : bestOverlapIndex;
        if (chosen >= 0) setActive(chosen);
    };

    const handlePointerDown = (e: React.PointerEvent) => {
        if (!carouselRef.current || count <= 1) return;
        isDraggingRef.current = true;
        movedRef.current = false;
        startXRef.current = e.clientX;
        startOffsetRef.current = getCurrentOffset();
        pointerIdRef.current = e.pointerId;
        (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
        setIsDragging(true);
        if (e.pointerType === 'mouse' && setPointer) {
            cursorMergedRef.current = true;
            setPointer(true);
        }
    };

    const handlePointerMove = (e: React.PointerEvent) => {
        if (!isDraggingRef.current || !carouselRef.current) return;
        const delta = startXRef.current - e.clientX;
        if (Math.abs(delta) > DRAG_THRESHOLD) {
            movedRef.current = true;
        }
        const maxOffset = getCarouselOffset(count - 1, windowSize);
        const next = Math.min(Math.max(startOffsetRef.current + delta, 0), maxOffset);
        carouselRef.current.style.transform = `translateX(-${next}px)`;
        updateActiveByVisibility();
    };

    const endDrag = (e: React.PointerEvent) => {
        if (!isDraggingRef.current) return;
        isDraggingRef.current = false;
        setIsDragging(false);
        if (pointerIdRef.current !== null && (e.target as HTMLElement).hasPointerCapture?.(pointerIdRef.current)) {
            (e.target as HTMLElement).releasePointerCapture(pointerIdRef.current);
        }
        updateActiveByVisibility();
        if (cursorMergedRef.current) {
            cursorMergedRef.current = false;
            setPointer?.(false);
        }
    };

    // Lets shift+wheel (or a trackpad's native horizontal swipe) scroll the
    // carousel. Needs a native, non-passive listener so preventDefault can
    // stop Safari from scrolling the page vertically when shift is held.
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleWheel = (e: WheelEvent) => {
            if (!carouselRef.current || count <= 1) return;
            const horizontalIntent = e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY);
            if (!horizontalIntent) return;
            e.preventDefault();

            const delta = e.deltaX !== 0 ? e.deltaX : e.deltaY;
            const maxOffset = getCarouselOffset(count - 1, windowSize);
            const next = Math.min(Math.max(getCurrentOffset() + delta, 0), maxOffset);
            carouselRef.current.style.transform = `translateX(-${next}px)`;
            updateActiveByVisibility();

            setIsWheeling(true);
            if (wheelIdleTimeoutRef.current) window.clearTimeout(wheelIdleTimeoutRef.current);
            wheelIdleTimeoutRef.current = window.setTimeout(() => setIsWheeling(false), WHEEL_IDLE_MS);
        };

        container.addEventListener('wheel', handleWheel, {passive: false});
        return () => {
            container.removeEventListener('wheel', handleWheel);
            if (wheelIdleTimeoutRef.current) window.clearTimeout(wheelIdleTimeoutRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [count, windowSize]);

    const handleImageClick = (url: string) => {
        if (movedRef.current) return;
        setPopup(true);
        setPopupImage(url);
    };

    return (
        <div
            id="project-carousel"
            className="pc"
            ref={containerRef}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onPointerLeave={e => isDraggingRef.current && endDrag(e)}>
            <div className={'carousel-wrapper ease-carousel' + (isDragging || isWheeling ? ' dragging' : '')} ref={carouselRef}>
                {value?.attributes?.Carousel?.data.map((images: any, index: number) => {
                    if (index > 0) {
                        return (
                            <div
                                ref={el => (imgRefs.current[index] = el)}
                                onClick={() => handleImageClick(process.env.REACT_APP_UPLOAD_URL + images?.attributes?.url)}
                                onMouseEnter={() => {
                                    setActive?.(index);
                                    setPointer(true);
                                }}
                                onMouseLeave={() => setPointer(false)}
                                className={'carousel-img view'}
                                key={'carousel-img' + index}>
                                <motion.div initial={{opacity: 0}} animate={{opacity: 1}} transition={pageTransition} className="motion ease">
                                    <ProgressiveImage
                                        src={process.env.REACT_APP_UPLOAD_URL + images?.attributes?.formats?.large?.url}
                                        thu={process.env.REACT_APP_UPLOAD_URL + images?.attributes?.formats?.thumbnail?.url}
                                        alt={project + '-img' + index}
                                        className={'ease'}
                                        draggable={false}
                                    />
                                </motion.div>
                            </div>
                        );
                    }

                    return (
                        <div
                            ref={el => (imgRefs.current[index] = el)}
                            onClick={() => handleImageClick(process.env.REACT_APP_UPLOAD_URL + images?.attributes?.url)}
                            onMouseEnter={() => {
                                setActive?.(index);
                                setPointer(true);
                            }}
                            onMouseLeave={() => setPointer(false)}
                            className={'carousel-img'}
                            key={'carousel-img' + index}>
                            <ProgressiveImage
                                src={process.env.REACT_APP_UPLOAD_URL + images?.attributes?.formats?.xlarge?.url}
                                thu={process.env.REACT_APP_UPLOAD_URL + images?.attributes?.formats?.thumbnail?.url}
                                alt={project + '-img0'}
                                draggable={false}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ProjectCarousel;
